<script setup>
import { computed } from 'vue';
import PDialog from '@/components/ProxifyUI/PDialog.vue';
import BaseConfirmationCard from '@/components/Base/BaseConfirmationCard.vue';
import BaseBadge from '@/components/Base/BaseBadge.vue';
import BaseButton from '../../Base/BaseButton.vue';
import { browserUtils } from '@/utils';
import moment from 'moment';
import BaseIcon from '../../Base/BaseIcon.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  duplicates: { type: Array, default: () => [] },
  isEditing: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'close']);

const isOpen = defineModel({
  type: Boolean,
  default: false,
});

const { openExternalLink } = browserUtils;

const close = () => {
  isOpen.value = false;
  emit('close');
};

const confirm = () => {
  emit('confirm');
  isOpen.value = false;
};

const hasNonRejected = computed(() => {
  return props.duplicates.some((dup) => !dup.is_rejected);
});

const formatDate = (isoString) => {
  if (!isoString) return '';
  const m = moment(isoString);
  return m.isValid() ? m.format('DD MMMM YYYY') : isoString;
};

const formatRejectionText = (duplicate) => {
  if (!duplicate.is_rejected || !duplicate.rejection) {
    return duplicate.stage_name;
  }

  const { rejected_by, created_at } = duplicate.rejection;

  const status = rejected_by ? 'Rejected' : 'Auto-Rejected';
  const date = created_at ? formatDate(created_at) : '';
  const by = rejected_by ? ` by ${rejected_by.name}` : '';

  return `${status} at ${duplicate.stage_name} on ${date}${by}`;
};

const platformMap = {
  LinkedIn: (dup) => dup.linkedin_link,
  GitHub: (dup) => dup.github_link,
};

const getPlatform = (duplicate) => {
  for (const key in platformMap) {
    if (duplicate.duplicated_by.includes(key)) {
      return { name: key, link: platformMap[key](duplicate) };
    }
  }
  return null;
};
</script>

<template>
  <PDialog
    v-model="isOpen"
    position="standard"
    persistent
    :style="{ zIndex: 9999 }"
  >
    <BaseConfirmationCard
      title="Duplicate Applications Found"
      :hide-footer="hasNonRejected"
      :confirm-button-text="
        isEditing ? 'Yes, update anyway' : 'Yes, create anyway'
      "
      confirm-button-state="danger"
      :cancel-button-text="isEditing ? `No, don't update` : `No, don't create`"
      cancel-button-state="primary"
      :on-confirm="confirm"
      :on-close="close"
      class="!max-w-[435px] w-full"
      heading-class="pb-4"
      width="435px"
      paddingless
      footer-border
    >
      <template #title>
        <div class="flex flex-col gap-4">
          <BaseBadge
            icon="alert-circle"
            icon-size="24px"
            color="warning"
            class="!w-12 !h-12 !rounded-full"
          ></BaseBadge>
        </div>
      </template>
      <div class="px-6">
        <div
          class="flex gap-2 text-body-lg text-proxify-gray-900 font-semibold"
        >
          <span>Duplicates found</span>
        </div>
        <div class="flex flex-col gap-6">
          <span class="font-normal">
            The new applicant you're trying to create shares one or more links
            with existing profiles in the system:
          </span>
          <div
            v-for="duplicate in duplicates"
            :key="duplicate.id"
            class="flex flex-col gap-1"
          >
            <div>
              <BaseBadge
                icon="user-03"
                icon-size="16px"
                color="transparent"
                class="!p-0 !h-auto"
              >
                <BaseButton
                  :to="`/applications/${duplicate.id}`"
                  target="_blank"
                  class="icon-link-color p-0 text-body-sm font-semibold min-h-0 overflow-hidden"
                  dense
                >
                  <div class="truncate">{{ duplicate.name }}</div>
                </BaseButton>
              </BaseBadge>
            </div>
            <div v-if="duplicate.duplicated_by">
              <BaseBadge
                icon="copy-06"
                icon-size="16px"
                color="transparent"
                class="!p-0"
              >
                <div class="flex gap-1">
                  <span>
                    {{
                      duplicate.duplicated_by
                        .replace(/LinkedIn link|GitHub link/, '')
                        .trim()
                    }}
                  </span>

                  <BaseButton
                    v-if="getPlatform(duplicate)"
                    target="_blank"
                    class="icon-link-color p-0 text-body-sm font-semibold min-h-0 overflow-hidden"
                    dense
                    @click="openExternalLink(getPlatform(duplicate).link)"
                  >
                    <div class="truncate">
                      {{ getPlatform(duplicate).name }}
                    </div>
                  </BaseButton>
                </div>
              </BaseBadge>
            </div>
            <div>
              <div class="flex gap-2">
                <div
                  class="max-w-[18px] h-4 w-full flex justify-center items-center"
                >
                  <BaseIcon
                    name="dot"
                    size="6px"
                    :color="
                      duplicate.is_rejected
                        ? 'proxify-error-500'
                        : 'proxify-success-500'
                    "
                  />
                </div>
                <div class="font-normal">
                  <div
                    v-if="duplicate.is_rejected && duplicate.rejection"
                    class="whitespace-normal !p-0"
                  >
                    {{ formatRejectionText(duplicate) }}
                    <span
                      v-for="reason in duplicate.rejection.reasons"
                      :key="reason.id"
                    >
                      · {{ reason.name }}
                    </span>
                  </div>
                  <span v-else>
                    {{ duplicate.stage_name }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="!hasNonRejected"
            class="flex text-body-sm text-proxify-gray-900 font-semibold"
          >
            <span>
              {{
                isEditing
                  ? 'Are you sure you want to update the applicant?'
                  : 'Are you sure you want to create the new applicant?'
              }}
            </span>
          </div>
        </div>
      </div>
    </BaseConfirmationCard>
  </PDialog>
</template>
